<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import Auth from 'dashboard/api/auth';
import {
  pauseRulesAPI,
  pauseSessionsAPI,
} from 'dashboard/api/talkviaAgentPauses';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import AppearanceSettingsModal from './AppearanceSettingsModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const store = useStore();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const { currentAccount } = useAccount();
const showAppearanceModal = ref(false);
const pauseRules = ref([]);
const activePause = ref(null);
const pauseDialogRef = ref(null);
const pauseBalloonRef = ref(null);
const isReturningFromPause = ref(false);
const pauseClockNow = ref(Date.now());
const pauseBalloonPosition = ref({ x: null, y: null });
const pauseBalloonDragOffset = ref({ x: 0, y: 0 });
const isDraggingPauseBalloon = ref(false);
const pauseDialog = reactive({
  title: '',
  description: '',
  canReturnFromPause: false,
});
const pauseActiveLabel = 'Em pausa';
const pauseNameLabel = 'Pausa';
const pauseElapsedLabel = 'Tempo em pausa';
const pauseMoveLabel = 'Mover balão da pausa';
const pauseBalloonViewportPadding = 16;
let pauseClockInterval;

const pauseBalloonStorageKey = computed(
  () =>
    `talkvia_pause_balloon_position_${currentUser.value?.id || 'user'}_${accountId.value || 'account'}`
);

const pauseBalloonStyle = computed(() => {
  const { x, y } = pauseBalloonPosition.value;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return {};

  return {
    left: `${x}px`,
    top: `${y}px`,
    right: 'auto',
    bottom: 'auto',
  };
});

const clampPauseBalloonPosition = (x, y) => {
  const balloon = pauseBalloonRef.value;
  if (!balloon) return { x, y };

  const maxX = Math.max(
    pauseBalloonViewportPadding,
    window.innerWidth - balloon.offsetWidth - pauseBalloonViewportPadding
  );
  const maxY = Math.max(
    pauseBalloonViewportPadding,
    window.innerHeight - balloon.offsetHeight - pauseBalloonViewportPadding
  );

  return {
    x: Math.min(Math.max(pauseBalloonViewportPadding, x), maxX),
    y: Math.min(Math.max(pauseBalloonViewportPadding, y), maxY),
  };
};

const savePauseBalloonPosition = () => {
  const { x, y } = pauseBalloonPosition.value;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;

  try {
    window.localStorage.setItem(
      pauseBalloonStorageKey.value,
      JSON.stringify({ x, y })
    );
  } catch {
    // Keep dragging available even when storage is unavailable.
  }
};

const keepPauseBalloonInViewport = () => {
  const { x, y } = pauseBalloonPosition.value;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;

  pauseBalloonPosition.value = clampPauseBalloonPosition(x, y);
  savePauseBalloonPosition();
};

const loadPauseBalloonPosition = async () => {
  try {
    const savedPosition = JSON.parse(
      window.localStorage.getItem(pauseBalloonStorageKey.value)
    );
    if (
      Number.isFinite(savedPosition?.x) &&
      Number.isFinite(savedPosition?.y)
    ) {
      pauseBalloonPosition.value = savedPosition;
      await nextTick();
      keepPauseBalloonInViewport();
      return;
    }
  } catch {
    // Use the default position when the saved value is unavailable or invalid.
  }

  pauseBalloonPosition.value = { x: null, y: null };
};

const handlePauseBalloonDragMove = event => {
  if (!isDraggingPauseBalloon.value) return;

  pauseBalloonPosition.value = clampPauseBalloonPosition(
    event.clientX - pauseBalloonDragOffset.value.x,
    event.clientY - pauseBalloonDragOffset.value.y
  );
};

const stopDraggingPauseBalloon = () => {
  if (!isDraggingPauseBalloon.value) return;

  isDraggingPauseBalloon.value = false;
  savePauseBalloonPosition();
  document.removeEventListener('pointermove', handlePauseBalloonDragMove);
  document.removeEventListener('pointerup', stopDraggingPauseBalloon);
  document.removeEventListener('pointercancel', stopDraggingPauseBalloon);
};

const startDraggingPauseBalloon = event => {
  if (event.button !== 0 || !pauseBalloonRef.value) return;

  const bounds = pauseBalloonRef.value.getBoundingClientRect();
  pauseBalloonDragOffset.value = {
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  };
  pauseBalloonPosition.value = { x: bounds.left, y: bounds.top };
  isDraggingPauseBalloon.value = true;

  document.addEventListener('pointermove', handlePauseBalloonDragMove);
  document.addEventListener('pointerup', stopDraggingPauseBalloon);
  document.addEventListener('pointercancel', stopDraggingPauseBalloon);
};

const activePauseName = computed(
  () =>
    activePause.value?.rule_name ||
    activePause.value?.rule?.name ||
    'Pausa ativa'
);

const activePauseIcon = computed(() => {
  const ruleId = activePause.value?.rule?.id;
  return (
    pauseRules.value.find(rule => rule.id === ruleId)?.icon || 'i-lucide-coffee'
  );
});

const pauseElapsedTime = computed(() => {
  const startedAt = new Date(activePause.value?.started_at || '').getTime();
  if (!Number.isFinite(startedAt)) return '00:00:00';

  const totalSeconds = Math.max(
    0,
    Math.floor((pauseClockNow.value - startedAt) / 1000)
  );
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    '0'
  );
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
});

const showPauseDialog = (title, description, canReturnFromPause = false) => {
  pauseDialog.title = title;
  pauseDialog.description = description;
  pauseDialog.canReturnFromPause = canReturnFromPause;
  pauseClockNow.value = Date.now();
  pauseDialogRef.value?.open();
};

const loadPauseState = async () => {
  try {
    const [rulesResponse, sessionsResponse] = await Promise.all([
      pauseRulesAPI.get(),
      pauseSessionsAPI.get({ active: 'true' }),
    ]);
    pauseRules.value = (rulesResponse.data || []).filter(rule => rule.active);
    activePause.value = sessionsResponse.data?.[0] || null;
  } catch {
    // Keep the last known state if the refresh fails.
  }
};

const startPause = async ruleId => {
  try {
    const { data } = await pauseSessionsAPI.start(ruleId);
    activePause.value = data;
    await store.dispatch('updateAvailability', {
      availability: 'busy',
      account_id: accountId.value,
    });
    showPauseDialog('Pausa iniciada', 'Seu status foi atualizado.', true);
  } catch {
    showPauseDialog(
      'Não foi possível iniciar a pausa',
      'Tente novamente em alguns instantes.'
    );
  }
};

const finishActivePause = async () => {
  const sessionId = activePause.value?.id;
  if (!sessionId) throw new Error('Active pause session not found');

  const { data: endedSession } = await pauseSessionsAPI.end(sessionId);
  if (endedSession?.active !== false) {
    throw new Error('Active pause session was not closed');
  }

  await store.dispatch('updateAvailability', {
    availability: 'online',
    account_id: accountId.value,
  });
  activePause.value = null;
};

const openActivePauseDialog = () => {
  if (!activePause.value) return;

  showPauseDialog(
    'Pausa em andamento',
    'Confira o tempo em pausa ou volte ao atendimento.',
    true
  );
};

const handlePauseDialogConfirm = async () => {
  if (!pauseDialog.canReturnFromPause) {
    pauseDialogRef.value?.close();
    return;
  }

  isReturningFromPause.value = true;
  try {
    await finishActivePause();
    pauseDialogRef.value?.close();
  } catch {
    pauseDialog.title = 'Não foi possível encerrar a pausa';
    pauseDialog.description = 'Tente novamente em alguns instantes.';
    pauseDialog.canReturnFromPause = false;
  } finally {
    isReturningFromPause.value = false;
  }
};

watch(accountId, currentAccountId => {
  if (currentAccountId) {
    loadPauseState();
    loadPauseBalloonPosition();
  }
});

watch(activePause, async pause => {
  if (!pause) return;

  await nextTick();
  keepPauseBalloonInViewport();
});

onMounted(() => {
  loadPauseState();
  loadPauseBalloonPosition();
  window.addEventListener('resize', keepPauseBalloonInViewport);
  pauseClockInterval = window.setInterval(() => {
    pauseClockNow.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  window.clearInterval(pauseClockInterval);
  stopDraggingPauseBalloon();
  window.removeEventListener('resize', keepPauseBalloonInViewport);
});

const planName = computed(
  () => currentAccount.value?.custom_attributes?.plan_name || null
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const toggleChatSupport = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: toggleChatSupport,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        showAppearanceModal.value = true;
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      dividerBefore: true,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: {
        name: 'api_docs_settings_index',
        params: { accountId: accountId.value },
      },
    },
    {
      show: false,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://talkvia.com.br/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      dividerBefore: true,
      danger: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-left cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
        <div v-if="!isCollapsed" class="min-w-0 flex-1">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div
            class="text-xs leading-4 truncate text-n-slate-11"
            :title="planName || currentUser.email"
          >
            {{ planName || currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody
      class="profile-menu bottom-12 z-50 mb-2 w-80 max-w-[calc(100vw-1rem)] ltr:left-0 rtl:right-0 [&>ul]:!gap-0.5 [&>ul]:!rounded-2xl [&>ul]:!border-n-weak [&>ul]:!bg-n-solid-1 [&>ul]:!p-2 [&>ul]:!shadow-2xl [&>ul]:!backdrop-blur-none"
    >
      <div
        class="mb-1 flex items-center gap-3 rounded-xl border border-n-brand/15 bg-n-brand/5 px-3 py-3"
      >
        <span
          class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-lucide-user-round-cog" class="size-[18px]" />
        </span>
        <div class="min-w-0">
          <p
            class="mb-0 text-[11px] font-semibold uppercase tracking-wide text-n-brand"
          >
            {{ t('SIDEBAR_ITEMS.PROFILE_SETTINGS') }}
          </p>
          <p class="mb-0 truncate text-sm font-semibold text-n-slate-12">
            {{ currentUser.available_name }}
          </p>
        </div>
      </div>
      <SidebarProfileMenuStatus
        :pause-rules="pauseRules"
        :active-pause="activePause"
        @start-pause="startPause"
        @open-active-pause="openActivePauseDialog"
        @refresh-pause-state="loadPauseState"
      />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownSeparator v-if="item.dividerBefore" class="!mx-0 !mb-2" />
          <DropdownItem
            v-if="item.show"
            :label="item.label"
            :icon="item.icon"
            :link="item.link"
            :native-link="item.nativeLink"
            :target="item.target"
            :click="item.click"
            class="group !cursor-pointer !gap-3 !rounded-xl !px-3 !py-2.5 transition-colors"
            :class="
              item.danger
                ? '!text-n-ruby-11 hover:!bg-n-ruby-3 [&_svg]:!text-n-ruby-11'
                : 'hover:!bg-n-brand/5'
            "
          >
            <span class="grid size-5 shrink-0 place-items-center">
              <Icon :icon="item.icon" class="size-4 text-n-slate-11" />
            </span>
            <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
            <Icon
              v-if="!item.danger"
              :icon="
                item.nativeLink
                  ? 'i-lucide-arrow-up-right'
                  : 'i-lucide-chevron-right'
              "
              class="size-3.5 shrink-0 text-n-slate-9 transition-transform group-hover:translate-x-0.5"
            />
          </DropdownItem>
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
  <Teleport to="body">
    <div
      v-if="activePause"
      ref="pauseBalloonRef"
      :style="pauseBalloonStyle"
      class="fixed bottom-5 z-[60] flex max-w-[calc(100vw-2rem)] select-none items-stretch overflow-hidden rounded-2xl border border-n-amber-6 bg-n-solid-2 text-left shadow-xl transition-shadow duration-200 ltr:left-5 rtl:right-5"
      :class="{ 'shadow-2xl': isDraggingPauseBalloon }"
    >
      <button
        type="button"
        class="grid w-8 shrink-0 touch-none cursor-grab place-items-center border-0 border-r border-n-weak bg-n-amber-3 text-n-amber-11 hover:bg-n-amber-4 active:cursor-grabbing"
        :title="pauseMoveLabel"
        :aria-label="pauseMoveLabel"
        @pointerdown.prevent="startDraggingPauseBalloon"
      >
        <Icon icon="i-lucide-grip-vertical" class="size-4" />
      </button>
      <button
        type="button"
        class="flex min-w-0 items-center gap-3 border-0 bg-transparent px-3 py-2.5 text-left transition-colors hover:bg-n-solid-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-amber-8"
        :title="`Pausa ${activePauseName} · ${pauseElapsedTime}`"
        @click="openActivePauseDialog"
      >
        <span
          class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-amber-3 text-n-amber-11"
        >
          <Icon :icon="activePauseIcon" class="size-[18px]" />
        </span>
        <span class="min-w-0">
          <span
            class="block max-w-40 truncate text-xs font-medium text-n-slate-12"
          >
            {{ activePauseName }}
          </span>
          <span class="block text-[11px] text-n-slate-10">
            {{ pauseActiveLabel }}
          </span>
        </span>
        <span
          class="shrink-0 rounded-lg bg-n-amber-3 px-2 py-1 font-mono text-sm font-semibold tabular-nums text-n-amber-11"
        >
          {{ pauseElapsedTime }}
        </span>
      </button>
    </div>
  </Teleport>
  <Dialog
    ref="pauseDialogRef"
    type="edit"
    width="md"
    :title="pauseDialog.title"
    :description="pauseDialog.description"
    cancel-button-label="Continuar em pausa"
    :confirm-button-label="
      pauseDialog.canReturnFromPause ? 'Voltar da pausa' : 'Entendi'
    "
    :show-cancel-button="pauseDialog.canReturnFromPause"
    :is-loading="isReturningFromPause"
    :close-on-click-outside="false"
    @confirm="handlePauseDialogConfirm"
  >
    <div
      v-if="pauseDialog.canReturnFromPause && activePause"
      class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
    >
      <div
        class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        <Icon :icon="activePauseIcon" class="size-5" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-n-slate-10">
          {{ pauseNameLabel }}
        </p>
        <p class="truncate text-sm font-semibold text-n-slate-12">
          {{ activePauseName }}
        </p>
      </div>
      <div class="shrink-0 text-right">
        <p class="text-xs font-medium text-n-slate-10">
          {{ pauseElapsedLabel }}
        </p>
        <p class="font-mono text-lg font-semibold tabular-nums text-n-slate-12">
          {{ pauseElapsedTime }}
        </p>
      </div>
    </div>
  </Dialog>
  <AppearanceSettingsModal
    :show="showAppearanceModal"
    @close="showAppearanceModal = false"
  />
</template>
